import { BasicIndex, type Collection } from "@tanstack/db";

import type { EntityStore } from "@/store/EntityStore";
import { recordRead } from "@/store/readTracking";

const EMPTY_KEYS: ReadonlySet<string> = new Set();

/**
 * Incrementally maintained secondary index: groups row keys by a single indexed field (e.g. items
 * by feedId). Backed by a TanStack collection index, which the live-query engine also uses for
 * predicate evaluation. Lookups return row keys; resolve rows through the store.
 */
export type EqualityIndex = {
  /** Row keys whose indexed field equals `value`; the empty set for null/undefined. */
  keys(value: string | null | undefined): ReadonlySet<string>;
};

/**
 * Creates an equality index on `field`. TanStack maintains it through upserts, re-keys, deletes,
 * and truncates, and builds it from current rows when created after data has loaded.
 *
 * A lookup records a read of the indexed collection. `rowsByIndex` resolves rows through the store
 * and would be attributed there anyway, but callers that only count keys never touch a row, and
 * neither does a lookup that matches none.
 */
export function createEqualityIndex<T extends Record<string, unknown>>(
  collection: Collection<T, string>,
  field: keyof T & string,
): EqualityIndex {
  const index = collection.createIndex((row) => row[field], { indexType: BasicIndex });
  if (!(index instanceof BasicIndex)) {
    throw new Error(`Expected a BasicIndex for field: ${field}`);
  }
  return {
    keys: (value) => {
      recordRead(collection.id);
      return value == null ? EMPTY_KEYS : (index.equalityLookup(value) as ReadonlySet<string>);
    },
  };
}

/**
 * Creates an equality index on a field holding several values per row: a row's key is filed under each value it holds.
 *
 * TanStack's `BasicIndex` files a row under one value, so this index is kept from the store's row changes instead, and
 * live-query predicates cannot use it; a live query filters such a field with `inArray`.
 *
 * @param store - The store whose row changes keep the index current.
 * @param name - The collection indexed.
 * @param valuesOf - The values a row is filed under; an empty list files it under none.
 */
export function createElementIndex<Schema extends Record<string, object>, K extends keyof Schema>(
  store: EntityStore<Schema>,
  name: K,
  valuesOf: (row: Schema[K]) => readonly string[],
): EqualityIndex {
  const keysByValue = new Map<string, Set<string>>();
  const valuesByKey = new Map<string, readonly string[]>();

  const unfile = (key: string): void => {
    for (const value of valuesByKey.get(key) ?? []) {
      const keys = keysByValue.get(value);
      keys?.delete(key);
      if (keys?.size === 0) keysByValue.delete(value);
    }
    valuesByKey.delete(key);
  };

  const file = (key: string, row: Schema[K] | undefined): void => {
    unfile(key);
    if (row === undefined) return;
    const values = valuesOf(row);
    valuesByKey.set(key, values);
    for (const value of values) {
      const keys = keysByValue.get(value);
      if (keys) keys.add(key);
      else keysByValue.set(value, new Set([key]));
    }
  };

  for (const [key, row] of store.getAll(name)) file(key, row);
  store.subscribeRowChanges((change) => {
    if (change.collection !== name) return;
    if (change.truncated) {
      keysByValue.clear();
      valuesByKey.clear();
    }
    for (const key of change.deleted) unfile(key);
    for (const key of change.upserted) file(key, store.get(name, key));
  });

  return {
    keys: (value) => {
      recordRead(name);
      return value == null ? EMPTY_KEYS : (keysByValue.get(value) ?? EMPTY_KEYS);
    },
  };
}
